<template>
  <div
    :class="
      cn('bg-black pb-12 pt-8 lg:pb-8 lg:pt-16 overflow-hidden', props.class)
    "
  >
    <div class="container mb-6">
      <h3 v-if="title" class="text-white ui-h3 uppercase">
        {{ title }}
      </h3>
    </div>
    <UiScrollableSlider
      v-if="!isFreeModeOnMobile"
      :slides-count="1.3"
      :items-list="products"
      class="product-list-carousel lg:!hidden"
      :gap-x="4"
    >
      <template #card="{ item, idx }">
        <CommonProductCardPlp
          :product="item"
          :product-index="idx"
          :plp-type="plpTypes.default"
          :statistic-item-list-name="statisticItemListName"
        />
      </template>
    </UiScrollableSlider>
    <div :class="['container', { 'max-lg:hidden': !isFreeModeOnMobile }]">
      <Swiper
        :slides-per-view="1.3"
        :modules="modules"
        :navigation="navigation"
        :free-mode="isFreeModeOnMobile"
        class="my-custom-swiper w-full !overflow-visible"
        :breakpoints="{
          [screens.lg]: {
            freeMode: false,
            slidesPerView: 4.7,
          },
        }"
        :space-between="4"
        @swiper="initSwiper"
        @resize="onUpdateSwiper"
      >
        <SwiperSlide
          v-for="(item, idx) in products?.slice(0, 15)"
          :key="item.id"
        >
          <CommonProductCardPlp
            :product="item"
            :product-index="idx"
            :plp-type="plpTypes.default"
            :statistic-item-list-name="statisticItemListName"
          />
        </SwiperSlide>
      </Swiper>
    </div>
    <div
      v-if="!hideArrows"
      class="container mt-4 text-right space-x-4 leading-none"
      :class="{ 'max-lg:hidden': !isFreeModeOnMobile }"
    >
      <button
        ref="prevEl"
        :aria-label="$t('previousSlide')"
        class="transition-all"
      >
        <UiIcon name="arrow_left" class="size-6 text-white" />
      </button>
      <button ref="nextEl" :aria-label="$t('nextSlide')" class="transition-all">
        <UiIcon name="arrow_right" class="size-6 text-white" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, FreeMode } from "swiper";
import { NavigationOptions } from "swiper/types";
import { debounce } from "lodash-es";
import { HTMLAttributes } from "vue";
import { CatalogData } from "ecom-types";
import { screens } from "@/configs/breakpointsConfig.json";
import { plpTypes } from "~/configs";

const props = withDefaults(
  defineProps<{
    title?: string;
    statisticItemListName?: string;
    products: CatalogData[];
    class?: HTMLAttributes["class"];
    isFreeModeOnMobile?: boolean;
  }>(),
  {
    title: "",
    statisticItemListName: "",
    class: "",
    isFreeModeOnMobile: true,
  },
);

const isMounted = useMounted();
const { viewItemListEvent } = useEvents();

const hideArrows = ref(true);

const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);

const debounceSwiper = debounce((params) => {
  hideArrows.value = params?.params?.slidesPerView >= props.products.length;
}, 200);

function initSwiper(params: any) {
  debounceSwiper(params);
}
function onUpdateSwiper(params: any) {
  debounceSwiper(params);
}

const navigation = reactive({
  prevEl,
  nextEl,
  disabledClass: "opacity-20 cursor-not-allowed",
}) as NavigationOptions;

const baseModules = [Navigation];
const modules = props.isFreeModeOnMobile
  ? [...baseModules, FreeMode]
  : baseModules;

watchEffect(() => {
  if (isMounted.value && props.products) {
    viewItemListEvent(props.products, {
      item_list_name: props.statisticItemListName,
    });
  }
});
</script>

<style scoped>
:deep(.product-list-carousel) .diamondshape-slick-mobile {
  @apply scroll-pl-0;
}
:deep(.product-list-carousel) .diamondshape-slick-mobile li {
  @apply first:ml-0 last:mr-0;
}
</style>
